/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { getFeedback, setUser as sentrySetUser } from '@sentry/browser'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import toast from 'react-hot-toast'

export type SentryFeedbackKind = 'bug' | 'feedback'

type Translate = (message: string) => string

type FeedbackThemeColors = {
  accentBackground?: string
  accentForeground?: string
  background?: string
  errorColor?: string
  foreground?: string
}

type OpenSentryFeedbackOptions = {
  kind: SentryFeedbackKind
  profileId?: string | null
  profileName?: string | null
  t: Translate
}

/**
 * Feedback is available whenever the web Sentry client is configured.
 * Session replay stays on its own consent and sampling rules.
 */
export function isSentryFeedbackEnabled(): boolean {
  const appConfig = getAppConfig()
  return Boolean(appConfig.SENTRY_DSN_WEB) && !appConfig.SENTRY_DISABLE
}

function readThemeToken(styles: CSSStyleDeclaration, name: string): string | undefined {
  const value = styles.getPropertyValue(name).trim()
  return value.length > 0 ? value : undefined
}

/** Shadow-DOM widget colors from the current app theme, including holiday overrides. */
function readFeedbackTheme(): FeedbackThemeColors {
  const styles = getComputedStyle(document.documentElement)
  return {
    background: readThemeToken(styles, '--popover'),
    foreground: readThemeToken(styles, '--popover-foreground'),
    accentBackground: readThemeToken(styles, '--primary'),
    accentForeground: readThemeToken(styles, '--primary-foreground'),
    errorColor: readThemeToken(styles, '--destructive'),
  }
}

function restoreSentryUser(profileId?: string | null) {
  if (profileId) {
    sentrySetUser({ id: profileId })
    return
  }
  sentrySetUser(null)
}

/**
 * Opens the Sentry feedback dialog. The profile name is applied only long enough
 * for the dialog to capture it, then the Sentry user returns to the profile id.
 */
export function openSentryFeedback(options: OpenSentryFeedbackOptions): void {
  openSentryFeedbackDialog(options).catch(() => {
    // The menu action has no further recovery if the dialog fails to open.
  })
}

async function openSentryFeedbackDialog({ kind, profileId, profileName, t }: OpenSentryFeedbackOptions) {
  if (!isSentryFeedbackEnabled()) {
    return
  }

  const feedback = getFeedback()
  if (!feedback) {
    return
  }

  const isBug = kind === 'bug'
  const theme = readFeedbackTheme()
  const colorScheme = document.documentElement.classList.contains('dark') ? 'dark' : 'light'

  sentrySetUser({
    id: profileId ?? undefined,
    username: profileName ?? '',
  })

  try {
    // Drop any previous host so the shadow stylesheet picks up the current theme.
    feedback.remove()
    const formOptions = {
      addScreenshotButtonLabel: t('Add screenshot'),
      cancelButtonLabel: t('Cancel'),
      colorScheme,
      confirmButtonLabel: t('Confirm'),
      emailLabel: t('Email (optional)'),
      emailPlaceholder: t('Enter your email'),
      enableScreenshot: true,
      errorEmptyMessageText: t('Please enter a message'),
      errorForbiddenText: t('Feedback could not be sent from this site'),
      errorGenericText: t('Unable to send feedback. Please try again.'),
      errorNoClientText: t('Feedback is unavailable right now'),
      errorTimeoutText: t('Sending feedback timed out. Please try again.'),
      formTitle: isBug ? t('Bug Report') : t('Feature Request'),
      hideToolText: t('Hide'),
      highlightToolText: t('Highlight'),
      isEmailRequired: false,
      isNameRequired: false,
      isRequiredLabel: t('(required)'),
      messageLabel: t('Message'),
      messagePlaceholder: isBug
        ? t('What went wrong? What did you expect to happen?')
        : t('What would you like us to know?'),
      nameLabel: t('Name (optional)'),
      namePlaceholder: t('Your name'),
      removeHighlightText: t('Remove'),
      removeScreenshotButtonLabel: t('Remove screenshot'),
      showEmail: true,
      showName: true,
      submitButtonLabel: isBug ? t('Send Bug Report') : t('Send Feature Request'),
      successMessageText: '',
      onSubmitSuccess: () => {
        toast.success(t('Thank you for your feedback'))
        feedback.remove()
      },
      tags: {
        feedback_type: isBug ? 'bug' : 'feature_request',
      },
    } satisfies Parameters<typeof feedback.createForm>[0]
    // Theme fields are merged at runtime; the public createForm type omits them.
    const form = await feedback.createForm({
      ...formOptions,
      themeDark: theme,
      themeLight: theme,
    } as Parameters<typeof feedback.createForm>[0])
    form.appendToDom()
    form.open()
  } finally {
    restoreSentryUser(profileId)
  }
}
